import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../redux';
import { updateBaseStyleId } from '../redux/reducer';
import type { BaseStyleId } from '../types/Styles';

type UseBaseStyleOverridesOptions = {
  /**
   * When provided, dispatches this style id once on mount and skips all other overrides.
   * Used by location pages to stay on alltrailsOutdoorsV2 while everything else defaults to V3.
   * @see https://alltrails.atlassian.net/browse/DISCO-25086
   */
  initialBaseStyleId?: BaseStyleId;
};

/**
 * Centralises base-style overrides that run after the Redux store is initialised.
 *
 * Location pages pin the style to alltrailsOutdoorsV2 via `initialBaseStyleId`.
 */
export default function useBaseStyleOverrides(options?: UseBaseStyleOverridesOptions) {
  const { initialBaseStyleId } = options ?? {};
  const dispatch = useAppDispatch();
  const baseStyleId = useAppSelector(state => state.map.baseStyleId);
  const hasAppliedInitialOverride = useRef(false);

  useEffect(() => {
    if (initialBaseStyleId && !hasAppliedInitialOverride.current) {
      hasAppliedInitialOverride.current = true;
      if (baseStyleId !== initialBaseStyleId) {
        dispatch(updateBaseStyleId(initialBaseStyleId));
      }
      return;
    }
  }, [baseStyleId, dispatch, initialBaseStyleId]);
}
